import { CartAddEvent, ThemeEvents } from '@theme/events';

class BoxBuilderComponent extends HTMLElement {
  /** @type {Map<string, {label: string, count: number}>} */
  #selections = new Map();

  #boxSize = 6;

  #abortController = new AbortController();

  /** @type {HTMLButtonElement|null} */
  #nativeSubmitBtn = null;

  connectedCallback() {
    this.#boxSize = parseInt(this.dataset.boxSize || '6', 10);
    this.#bindEvents();
    this.#bindNativeForm();
    this.#updateUI();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #getBoxVariantId() {
    const section = this.closest('.shopify-section');
    const formInput = section?.querySelector('product-form-component input[name="id"]');
    return formInput?.value || this.dataset.boxVariantId || null;
  }

  #getVariantId(card) {
    const select = card.querySelector('[data-variant-select]');
    return select ? select.value : card.dataset.variantId;
  }

  #getLabel(card) {
    const productTitle = card.dataset.productTitle || '';
    const select = card.querySelector('[data-variant-select]');
    if (!select || !select.value) return productTitle;
    const variantTitle = select.options[select.selectedIndex]?.text || '';
    return variantTitle && variantTitle !== 'Default Title'
      ? `${productTitle} - ${variantTitle}`
      : productTitle;
  }

  #isCardReady(card) {
    if (card.hasAttribute('data-sold-out')) return false;
    const select = card.querySelector('[data-variant-select]');
    if (select && !select.value) return false;
    return true;
  }

  #totalCount() {
    let total = 0;
    this.#selections.forEach((v) => (total += v.count));
    return total;
  }

  #increment(card) {
    if (!this.#isCardReady(card)) return;
    if (this.#totalCount() >= this.#boxSize) return;
    const id = this.#getVariantId(card);
    const label = this.#getLabel(card);
    const existing = this.#selections.get(id);
    if (existing) {
      existing.count += 1;
    } else {
      this.#selections.set(id, { label, count: 1 });
    }
    this.#updateUI();
  }

  #decrement(card) {
    const id = this.#getVariantId(card);
    const existing = this.#selections.get(id);
    if (!existing || existing.count === 0) return;
    existing.count -= 1;
    if (existing.count === 0) this.#selections.delete(id);
    this.#updateUI();
  }

  #bindEvents() {
    this.querySelectorAll('[data-box-increment]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const card = btn.closest('[data-card]');
        if (card) this.#increment(card);
      });
    });

    this.querySelectorAll('[data-box-decrement]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const card = btn.closest('[data-card]');
        if (card) this.#decrement(card);
      });
    });

    this.querySelectorAll('[data-variant-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const card = select.closest('[data-card]');
        if (!card) return;
        const oldId = card.dataset.activeVariantId;
        const newId = select.value;
        if (oldId && oldId !== newId && this.#selections.has(oldId)) {
          const existing = this.#selections.get(oldId);
          this.#selections.delete(oldId);
          if (newId) {
            const label = this.#getLabel(card);
            this.#selections.set(newId, { label, count: existing.count });
          }
        }
        card.dataset.activeVariantId = newId || '';
        this.#updateUI();
      });
    });
  }

  #bindNativeForm() {
    const section = this.closest('.shopify-section');
    const form = section?.querySelector('product-form-component form, form[action*="/cart/add"]');
    if (!form) return;

    this.#nativeSubmitBtn = form.querySelector('[name="add"], button[type="submit"]');

    // Intercept both click (theme handlers) and submit (form fallback)
    const handler = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.#addToCart();
    };

    this.#nativeSubmitBtn?.addEventListener('click', handler, {
      signal: this.#abortController.signal,
      capture: true,
    });

    form.addEventListener('submit', handler, {
      signal: this.#abortController.signal,
      capture: true,
    });
  }

  #updateUI() {
    const total = this.#totalCount();
    const ready = total === this.#boxSize;

    const counter = this.querySelector('[data-box-counter]');
    if (counter) counter.textContent = `${total} / ${this.#boxSize}`;

    if (this.#nativeSubmitBtn) {
      this.#nativeSubmitBtn.disabled = !ready;
      this.#nativeSubmitBtn.setAttribute('aria-disabled', String(!ready));
    }

    this.querySelectorAll('[data-card]').forEach((card) => {
      if (card.hasAttribute('data-sold-out')) return;

      const id = this.#getVariantId(card);
      const count = id ? (this.#selections.get(id)?.count || 0) : 0;
      const cardReady = this.#isCardReady(card);

      const badge = card.querySelector('[data-box-badge]');
      if (badge) {
        badge.textContent = count > 0 ? String(count) : '';
        badge.hidden = count === 0;
      }

      const qtyEl = card.querySelector('[data-box-qty]');
      if (qtyEl) qtyEl.textContent = String(count);

      const incBtn = card.querySelector('[data-box-increment]');
      if (incBtn) incBtn.disabled = !cardReady || (total >= this.#boxSize && count === 0);

      const decBtn = card.querySelector('[data-box-decrement]');
      if (decBtn) decBtn.disabled = count === 0;
    });
  }

  #buildProperties() {
    /** @type {string[]} */
    const entries = [];
    this.#selections.forEach(({ label, count }) => {
      for (let i = 0; i < count; i++) entries.push(label);
    });
    /** @type {Record<string, string>} */
    const props = {};
    entries.forEach((label, i) => {
      props[`Prodotto ${i + 1}`] = label;
    });
    return props;
  }

  async #addToCart() {
    const boxVariantId = this.#getBoxVariantId();
    if (!boxVariantId || this.#totalCount() !== this.#boxSize) return;

    const errorEl = this.querySelector('[data-box-error]');

    if (this.#nativeSubmitBtn) this.#nativeSubmitBtn.disabled = true;
    if (errorEl) errorEl.hidden = true;

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: boxVariantId,
          quantity: 1,
          properties: this.#buildProperties(),
        }),
      });

      const data = await res.json();

      if (!res.ok || data.status) {
        if (errorEl) {
          errorEl.textContent = data.message || "Errore durante l'aggiunta al carrello.";
          errorEl.hidden = false;
        }
        if (this.#nativeSubmitBtn) this.#nativeSubmitBtn.disabled = false;
        return;
      }

      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();

      document.dispatchEvent(
        new CartAddEvent(cart, this.id, { itemCount: cart.item_count, variantId: boxVariantId })
      );

      this.#reset();
    } catch (err) {
      console.error('[box-builder]', err);
      if (this.#nativeSubmitBtn) this.#nativeSubmitBtn.disabled = false;
      if (errorEl) {
        errorEl.textContent = 'Errore di rete. Riprova.';
        errorEl.hidden = false;
      }
    }
  }

  #reset() {
    this.#selections.clear();
    this.querySelectorAll('[data-variant-select]').forEach((select) => {
      select.value = '';
      const card = select.closest('[data-card]');
      if (card) card.dataset.activeVariantId = '';
    });
    this.#updateUI();
  }
}

customElements.define('box-builder-component', BoxBuilderComponent);
